import type { Human } from '../human/types';

export type Expression = Partial<Record<string, number>>;

/**
 * Mixeur d'expressions : plusieurs couches nommées (humeur, réaction, paupières, parole…)
 * sont sommées puis lissées par canal, et poussées vers les morphs du personnage.
 * Les canaux "rapides" (clignements, visèmes) ne sont pas lissés.
 */
export class FaceMixer {
  private layers = new Map<string, { weights: Expression; gain: number }>();
  private current = new Map<string, number>();
  private target = new Map<string, number>();
  private available: Set<string>;
  /** Vitesse de lissage (1/s) par défaut ; plus haut = plus réactif. */
  smoothing = 7;
  private fast = /^(eyeBlink|viseme_|jawOpen)/;
  /** Poids lissés des unités faciales osseuses (clés "bu:Nom" dans les couches). */
  readonly bones = new Map<string, number>();

  constructor(private human: Human) {
    this.available = new Set(human.morphNames);
  }

  setLayer(name: string, weights: Expression, gain = 1) {
    this.layers.set(name, { weights, gain });
  }

  setGain(name: string, gain: number) {
    const l = this.layers.get(name);
    if (l) l.gain = gain;
  }

  clearLayer(name: string) {
    this.layers.delete(name);
  }

  has(name: string) {
    return this.available.has(name);
  }

  update(dt: number) {
    this.target.clear();
    for (const { weights, gain } of this.layers.values()) {
      if (gain <= 0) continue;
      for (const k in weights) {
        const w = weights[k]!;
        this.target.set(k, (this.target.get(k) ?? 0) + w * gain);
      }
    }
    const a = 1 - Math.exp(-this.smoothing * dt);
    // Canaux actifs, puis canaux en train de revenir à zéro.
    for (const k of this.target.keys()) this.step(k, a);
    for (const k of this.current.keys()) if (!this.target.has(k)) this.step(k, a);
  }

  private step(k: string, a: number) {
    const bone = k.startsWith('bu:');
    if (!bone && !this.available.has(k)) return;
    // Les unités osseuses MakeHuman sont timides : on autorise jusqu'à 2,5 (les morphs restent bornés à 1).
    const t = Math.min(bone ? 2.5 : 1, Math.max(0, this.target.get(k) ?? 0));
    const c = this.current.get(k) ?? 0;
    let n = this.fast.test(k) ? t : c + (t - c) * a;
    if (t === 0 && n < 2e-3) n = 0;
    if (bone) {
      // Unité faciale osseuse : lue par FaceUnits.apply (pas de morph).
      const name = k.slice(3);
      if (n === 0) this.bones.delete(name); else this.bones.set(name, n);
    } else if (n !== c) this.human.setMorph(k, n);
    if (n === 0) this.current.delete(k);
    else this.current.set(k, n);
  }
}
